import React, { useCallback, useState } from 'react'
import clsx from 'clsx'
import { ChevronDown, ChevronRight, Eye, EyeOff, RefreshCw } from 'lucide-react'
import type {
    KitConvoDebugScoringDto,
    KitConvoDebugScoringRubricSectionDto,
    KitConvoDebugCriterionScoreDto,
    KitConvoDebugScoringCriterionDto,
} from '@/api/kit/KitApi'
import KitApi from '@/api/kit/KitApi'
import Badge from '../../common/badge/Badge'
import { BadgeColor } from '../../common/badge/badgeTypes'
import RubricScoreBadge from '../../jobScreeners/components/RubricScoreBadge'
import type { ScoreType, ScoreValue } from '../../jobScreeners/types'

// ---------------------------------------------------------------------------
// Section accent colours (copied from CandidatesTable — separate feature)
// ---------------------------------------------------------------------------

const SECTION_ACCENTS: { headerBg: string; borderColor: string }[] = [
    { headerBg: 'bg-blue-50/80', borderColor: 'border-l-blue-300' },
    { headerBg: 'bg-slate-50/80', borderColor: 'border-l-slate-300' },
    { headerBg: 'bg-amber-50/70', borderColor: 'border-l-amber-300' },
    { headerBg: 'bg-emerald-50/70', borderColor: 'border-l-emerald-300' },
]

// ---------------------------------------------------------------------------
// Score helpers (copied from CandidatesTable — separate feature)
// ---------------------------------------------------------------------------

function parseScoreValue(raw: string): ScoreValue {
    if (raw === 'pass' || raw === 'fail' || raw === 'flag') {
        return raw
    }
    if (raw === 'not_asked' || raw === 'not_answered') {
        return raw
    }
    const num = parseInt(raw, 10)
    if (!isNaN(num) && num >= 1 && num <= 3) {
        return num as 1 | 2 | 3
    }
    return null
}

function scoreToNumeric(
    value: ScoreValue,
    scoreType: ScoreType,
): { actual: number; max: number } {
    if (value === null) return { actual: 0, max: 0 }
    if (value === 'not_asked') return { actual: 0, max: 0 }
    if (value === 'not_answered') return { actual: 0, max: 1 }
    if (scoreType === 'numeric-1-3') {
        return { actual: value as number, max: 3 }
    }
    // binary and pass-flag-fail
    if (value === 'pass') return { actual: 1, max: 1 }
    if (value === 'flag') return { actual: 0.5, max: 1 }
    return { actual: 0, max: 1 } // fail
}

// ---------------------------------------------------------------------------
// Flatten criteria helper
// ---------------------------------------------------------------------------

type FlatCriterion = {
    section: KitConvoDebugScoringRubricSectionDto
    sectionIndex: number
    criterion: KitConvoDebugScoringCriterionDto
    isFirstInSection: boolean
}

function flattenCriteria(
    rubric: KitConvoDebugScoringRubricSectionDto[],
): FlatCriterion[] {
    return rubric.flatMap((section, sectionIndex) =>
        section.criteria.map((criterion, criterionIndex) => ({
            section,
            sectionIndex,
            criterion,
            isFirstInSection: criterionIndex === 0,
        })),
    )
}

function calculateOverallPercent(
    scores: KitConvoDebugCriterionScoreDto[],
    allCriteria: FlatCriterion[],
): number {
    let totalActual = 0
    let totalMax = 0
    const scoreMap = new Map(scores.map((s) => [s.criterionId, s]))

    for (const { criterion } of allCriteria) {
        const entry = scoreMap.get(criterion.id)
        if (!entry) continue
        const parsed = parseScoreValue(entry.scoreValue)
        const { actual, max } = scoreToNumeric(
            parsed,
            criterion.scoreType as ScoreType,
        )
        totalActual += actual
        totalMax += max
    }

    return totalMax === 0 ? 0 : Math.round((totalActual / totalMax) * 100)
}

// ---------------------------------------------------------------------------
// Timestamp formatter
// ---------------------------------------------------------------------------

function formatTimestamp(value: string): string {
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleString()
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

type Props = {
    scoring: KitConvoDebugScoringDto | null
    kitConvoId: string | null
    onRetriggerComplete: () => void
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ScoringResultsSection({
    scoring,
    kitConvoId,
    onRetriggerComplete,
}: Props): React.JSX.Element {
    const [isOpen, setIsOpen] = useState(true)
    const [showJustifications, setShowJustifications] = useState(false)
    const [isRetriggering, setIsRetriggering] = useState(false)
    const [retriggerResult, setRetriggerResult] = useState<{
        status: 'completed' | 'failed'
        message: string | null
    } | null>(null)

    const handleRetrigger = useCallback(async () => {
        if (!kitConvoId || isRetriggering) return

        setIsRetriggering(true)
        setRetriggerResult(null)

        try {
            const result = await KitApi.retriggerScoring({ kitConvoId })
            setRetriggerResult({
                status: result.status,
                message: result.message,
            })
            onRetriggerComplete()
        } catch (error) {
            setRetriggerResult({
                status: 'failed',
                message:
                    error instanceof Error
                        ? error.message
                        : 'Unknown error occurred',
            })
        } finally {
            setIsRetriggering(false)
        }
    }, [kitConvoId, isRetriggering, onRetriggerComplete])

    const attempt = scoring?.attempt ?? null
    const rubric = scoring?.rubric ?? []
    const allCriteria = flattenCriteria(rubric)
    const scoreMap = new Map(
        (attempt?.scores ?? []).map((s) => [s.criterionId, s]),
    )

    return (
        <div className="mt-4 rounded-2xl border border-border bg-white p-4 shadow-[var(--shadow-soft)]">
            {/* Section header */}
            <div className="flex items-center justify-between gap-2">
                <button
                    type="button"
                    onClick={() => setIsOpen((prev) => !prev)}
                    className="flex items-center gap-1.5 text-sm font-semibold text-foreground hover:text-foreground/80 transition-colors"
                >
                    {isOpen ? (
                        <ChevronDown className="h-4 w-4" />
                    ) : (
                        <ChevronRight className="h-4 w-4" />
                    )}
                    Scoring Results
                </button>

                <div className="flex items-center gap-2">
                    {retriggerResult ? (
                        <span
                            className={clsx(
                                'text-xs',
                                retriggerResult.status === 'completed'
                                    ? 'text-emerald-700'
                                    : 'text-red-600',
                            )}
                        >
                            {retriggerResult.status === 'completed'
                                ? 'Scoring completed'
                                : `Failed: ${retriggerResult.message ?? 'Unknown error'}`}
                        </span>
                    ) : null}
                    {kitConvoId ? (
                        <button
                            type="button"
                            onClick={handleRetrigger}
                            disabled={isRetriggering}
                            className={clsx(
                                'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
                                isRetriggering
                                    ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
                            )}
                        >
                            <RefreshCw
                                className={clsx(
                                    'h-3 w-3',
                                    isRetriggering && 'animate-spin',
                                )}
                            />
                            {isRetriggering
                                ? 'Re-scoring...'
                                : 'Re-trigger Scoring'}
                        </button>
                    ) : null}
                </div>
            </div>

            {/* Collapsible body */}
            {isOpen ? (
                <div className="mt-3">
                    {renderBody(
                        attempt,
                        rubric,
                        allCriteria,
                        scoreMap,
                        showJustifications,
                        setShowJustifications,
                    )}
                </div>
            ) : null}
        </div>
    )
}

// ---------------------------------------------------------------------------
// Body render — delegates to the three states
// ---------------------------------------------------------------------------

function renderBody(
    attempt: KitConvoDebugScoringDto['attempt'],
    rubric: KitConvoDebugScoringRubricSectionDto[],
    allCriteria: FlatCriterion[],
    scoreMap: Map<string, KitConvoDebugCriterionScoreDto>,
    showJustifications: boolean,
    setShowJustifications: React.Dispatch<React.SetStateAction<boolean>>,
): React.JSX.Element {
    // State 1 — no data
    if (!attempt) {
        return (
            <p className="text-xs text-muted-foreground">
                No scoring results for this conversation. Use Re-trigger Scoring
                to score the transcript.
            </p>
        )
    }

    // State 2 — failed attempt
    if (attempt.status === 'FAILED') {
        return (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                <div className="flex items-center gap-2">
                    <Badge color={BadgeColor.Error}>Failed</Badge>
                    <span className="text-xs text-gray-500">
                        {formatTimestamp(attempt.createdAt)} &middot;{' '}
                        {attempt.llmModel}
                    </span>
                </div>
                {attempt.errorMessage ? (
                    <p className="mt-2 text-sm font-medium text-red-800">
                        {attempt.errorMessage}
                    </p>
                ) : null}
                {attempt.errorDetail ? (
                    <p className="mt-1 text-xs text-red-600/80">
                        {attempt.errorDetail}
                    </p>
                ) : null}
            </div>
        )
    }

    // State 3 — completed attempt
    const overallPercent = calculateOverallPercent(attempt.scores, allCriteria)

    return (
        <div>
            {/* Justification toggle */}
            <div className="mb-3 flex items-center gap-2">
                <button
                    type="button"
                    onClick={() => setShowJustifications((prev) => !prev)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                    {showJustifications ? (
                        <EyeOff className="h-3 w-3" />
                    ) : (
                        <Eye className="h-3 w-3" />
                    )}
                    {showJustifications
                        ? 'Hide Justifications'
                        : 'Show Justifications'}
                </button>
            </div>

            {/* Score table */}
            <div className="overflow-auto rounded-lg border border-gray-200">
                <table className="w-full table-fixed border-collapse text-sm">
                    <thead>
                        {/* Row 1: section group headers */}
                        <tr className="border-b border-gray-200">
                            <th
                                rowSpan={2}
                                className="w-16 bg-gray-50 px-3 py-2.5 text-center text-xs font-semibold uppercase tracking-wider text-gray-500 border-b border-gray-200"
                            >
                                Score
                            </th>
                            {rubric.map((section, idx) => {
                                const accent =
                                    SECTION_ACCENTS[
                                        idx % SECTION_ACCENTS.length
                                    ]
                                return (
                                    <th
                                        key={section.id}
                                        colSpan={section.criteria.length}
                                        className={clsx(
                                            accent.headerBg,
                                            'text-center text-[11px] font-semibold uppercase tracking-wider text-gray-600',
                                            'px-2 py-2.5',
                                            'border-b border-gray-200',
                                            'border-l-2',
                                            accent.borderColor,
                                        )}
                                    >
                                        {section.label}
                                    </th>
                                )
                            })}
                        </tr>

                        {/* Row 2: criterion sub-headers */}
                        <tr className="border-b border-gray-100">
                            {allCriteria.map(
                                ({
                                    criterion,
                                    sectionIndex,
                                    isFirstInSection,
                                }) => {
                                    const accent =
                                        SECTION_ACCENTS[
                                            sectionIndex %
                                                SECTION_ACCENTS.length
                                        ]
                                    const displayName =
                                        criterion.shortName ||
                                        (criterion.label.length > 15
                                            ? criterion.label.slice(0, 15) +
                                              '...'
                                            : criterion.label)
                                    return (
                                        <th
                                            key={criterion.id}
                                            className={clsx(
                                                'text-[11px] font-medium text-gray-500 text-center',
                                                'px-3 py-2',
                                                'bg-gray-50/50',
                                                isFirstInSection &&
                                                    'border-l-2',
                                                isFirstInSection &&
                                                    accent.borderColor,
                                            )}
                                            title={criterion.label}
                                        >
                                            {displayName}
                                        </th>
                                    )
                                },
                            )}
                        </tr>
                    </thead>

                    <tbody>
                        <tr className="border-b border-gray-100 last:border-b-0">
                            {/* Score % cell */}
                            <td className="px-3 py-2.5 text-center">
                                <span className="font-semibold text-sm text-gray-900">
                                    {overallPercent}%
                                </span>
                            </td>

                            {/* Score cells */}
                            {allCriteria.map(
                                ({
                                    criterion,
                                    sectionIndex,
                                    isFirstInSection,
                                }) => {
                                    const accent =
                                        SECTION_ACCENTS[
                                            sectionIndex %
                                                SECTION_ACCENTS.length
                                        ]
                                    const scoreEntry = scoreMap.get(
                                        criterion.id,
                                    )
                                    const parsedValue = scoreEntry
                                        ? parseScoreValue(scoreEntry.scoreValue)
                                        : null

                                    return (
                                        <td
                                            key={criterion.id}
                                            className={clsx(
                                                'px-3 py-2.5 text-center',
                                                isFirstInSection &&
                                                    'border-l-2',
                                                isFirstInSection &&
                                                    accent.borderColor,
                                            )}
                                        >
                                            <div className="flex flex-col items-center gap-1">
                                                <RubricScoreBadge
                                                    scoreType={
                                                        criterion.scoreType as ScoreType
                                                    }
                                                    value={parsedValue}
                                                />
                                                {showJustifications &&
                                                scoreEntry ? (
                                                    <div className="mt-1 max-w-[140px]">
                                                        <p className="text-[10px] leading-tight text-gray-500">
                                                            {
                                                                scoreEntry.justification
                                                            }
                                                        </p>
                                                        {scoreEntry.detail ? (
                                                            <p className="mt-0.5 text-[10px] italic leading-tight text-gray-400">
                                                                {
                                                                    scoreEntry.detail
                                                                }
                                                            </p>
                                                        ) : null}
                                                    </div>
                                                ) : null}
                                            </div>
                                        </td>
                                    )
                                },
                            )}
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Attempt metadata */}
            <p className="mt-2 text-[11px] text-gray-400">
                Scored at {formatTimestamp(attempt.createdAt)} using{' '}
                {attempt.llmModel}
            </p>
        </div>
    )
}
